import React from 'react';
import { Star, ArrowUpRightSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useEffect } from 'react';

const testimonials = [
  {
    name: "Kris K.",
    image: "https://dynamic-media-cdn.tripadvisor.com/media/photo-o/1a/f6/e7/3d/default-avatar-2020-55.jpg?w=300&h=-1&s=1",
    text: "Sierpe Frogs delivers an extraordinary experience! Raby and Jeffrey's dedication to ethical wildlife photography and their deep respect for nature set them apart. They go above and beyond, tailoring the tour to your skill level and interests while ensuring every moment is unforgettable."
  },
  {
    name: "Camila S.",
    image: "https://dynamic-media-cdn.tripadvisor.com/media/photo-o/1a/f6/e2/e6/default-avatar-2020-45.jpg?w=300&h=-1&s=1",
    text: "Absolutely unforgettable experience!! Jeffrey is so knowledgeable and very skilled at spotting wildlife that I never would've been able to spot myself in a million years. I saw such an incredible variety of frogs that I was super excited to see for the first time, plus some amazing reptiles!"
  },
  {
    name: "Esther R.",
    image: "https://dynamic-media-cdn.tripadvisor.com/media/photo-o/1a/f6/e9/ef/default-avatar-2020-66.jpg?w=300&h=-1&s=1",
    text: "Best night trip of our holiday. Went together with my son. Jeffrey has so much knowledge! And we saw so much different frogs! Beside frogs we also saw plenty other animals. The trip supposed to last for about 3 hours but we came back after almost 5! No rush at all and you could easily see that Jeffrey enjoyed it a lot to show us the beauty of nature during the night! Worth every single Colon :-)"
  },
  {
    name: "Nick S.",
    image: "https://dynamic-media-cdn.tripadvisor.com/media/photo-o/1a/f6/ef/9c/default-avatar-2020-14.jpg?w=300&h=-1&s=1",
    text: "We had multiple trips with Raby. We did the night frog tour, river wildlife tour, Corcovado tour, and a day of fishing. All of the activities were fantastic and Raby will do everything possible to find the animals you want to see most.\n\nThe night tour was the stand out. We saw numerous frogs and snakes and were able to get amazing pictures."
  }
];

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [visibleTestimonials, setVisibleTestimonials] = useState<Set<number>>(new Set());

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', checkMobile);
    checkMobile();

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-testimonial-index'));
            if (!isNaN(index)) {
              setVisibleTestimonials((prev) => new Set([...prev, index]));
            }
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '50px'
      }
    );

    document.querySelectorAll('[data-testimonial-index]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [testimonials]);

  const next = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + (isMobile ? 1 : 2) >= testimonials.length ? 0 : prev + (isMobile ? 1 : 2)));
  };

  const prev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => {
      if (isMobile) {
        return prev - 1 < 0 ? testimonials.length - 1 : prev - 1;
      }
      return prev - 2 < 0 ? testimonials.length - 2 : prev - 2;
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsAnimating(false), 500);
    return () => clearTimeout(timer);
  }, [currentIndex]);

  return (
    <div id="testimonials" className="space-y-12">
      {/* Review Stats */}
      <div className="flex flex-col items-center space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 w-full max-w-4xl my-2">
          {/* TripAdvisor */}
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-2 flex items-center justify-center gap-2">
              TripAdvisor reviews
            </h3>
            <div className="flex justify-center gap-1 mb-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <div className="flex items-center gap-4 justify-center text-lg">
              <span className="font-semibold">115 reviews</span>
            </div>
            <a
              href="https://www.tripadvisor.com/Attraction_Review-g2642913-d10023190-Reviews-Sierpefrogs-Sierpe_Province_of_Puntarenas.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm mt-2 text-white/70 flex items-center justify-center gap-1 hover:text-white/90 transition-colors hover:underline"
            >
              Want to read all our reviews? Click here
              <ArrowUpRightSquare className="w-4 h-4" />
            </a>
          </div>

          {/* Google Reviews */}
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-2 flex items-center justify-center gap-2">
              Google Reviews
            </h3>
            <div className="flex justify-center gap-1 mb-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <div className="flex items-center gap-4 justify-center text-lg">
              <span className="font-semibold">30 reviews</span>
            </div>
            <a
              href="https://www.google.com/search?q=sierpe+frogs"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm mt-2 text-white/70 flex items-center justify-center gap-1 hover:text-white/90 transition-colors hover:underline"
            >
              Want to read all our reviews? Click here
              <ArrowUpRightSquare className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Testimonials Grid */}
      <div className="relative max-w-[90vw] mx-auto">
        <div className="overflow-hidden">
          <div 
            className="flex transition-transform duration-500 ease-out w-full"
            style={{ transform: `translateX(-${currentIndex * (isMobile ? 100 : 50)}%)` }}
          >
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className={`w-full ${!isMobile && 'md:w-1/2'} flex-shrink-0 px-8 md:px-12`}
                data-testimonial-index={index}
              >
                <div className={`p-6 bg-[#1F2B1F] hover:bg-[#243024] transition-all flex flex-col items-center rounded-[1rem] h-full transform ${
                  visibleTestimonials.has(index) 
                    ? 'translate-y-0 opacity-100' 
                    : 'translate-y-16 opacity-0'
                } duration-700`}>
                  {/* Avatar */}
                  <div className="w-16 h-16 mb-4 overflow-hidden rounded-[1rem]">
                    <img 
                      src={testimonial.image}
                      alt="Testimonial"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Reviewer Name */}
                  <p className="font-semibold mb-4">{testimonial.name}</p>

                  {/* Star Rating */}
                  <div className="flex justify-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>

                  {/* Review Text */}
                  <p className="text-xs opacity-70 text-center flex-grow whitespace-pre-line leading-relaxed">"{testimonial.text}"</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Buttons */}
        <button 
          onClick={prev}
          aria-label="View previous testimonial"
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-8 w-12 h-12 flex items-center justify-center text-white hover:text-white/70 transition-colors disabled:opacity-50"
          disabled={isAnimating}
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
        <button 
          onClick={next}
          aria-label="View next testimonial"
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-8 w-12 h-12 flex items-center justify-center text-white hover:text-white/70 transition-colors disabled:opacity-50"
          disabled={isAnimating}
        >
          <ChevronRight className="w-8 h-8" />
        </button>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: isMobile ? testimonials.length : testimonials.length / 2 }).map((_, index) => (
            <button
              key={index}
              onClick={() => {
                if (!isAnimating) {
                  setIsAnimating(true);
                  setCurrentIndex(index * (isMobile ? 1 : 2));
                }
              }}
              aria-label={`Testimonial ${index + 1} of ${isMobile ? testimonials.length : testimonials.length / 2}`}
              className={`w-8 h-8 rounded-full transition-all ${
                currentIndex === index * (isMobile ? 1 : 2)
                  ? 'bg-white' 
                  : 'bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Testimonials;